import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema.gen';
import { useAuth } from '@/store/auth';
import { refreshSession, noteActivity } from '@/lib/session';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Middleware d'authentification :
 *  - pose le jeton d'accès en Bearer ;
 *  - sur un 401, demande UN renouvellement et rejoue la requête.
 *
 * Le renouvellement n'est plus lancé ici : il passe par `refreshSession`, qui
 * n'en laisse courir qu'un seul à la fois, y compris entre onglets. Ce détour
 * n'est pas une élégance — le serveur fait désormais tourner le jeton de
 * renouvellement, et lui en présenter deux fois le même est traité comme un
 * rejeu, donc ferme la session. L'ancien code, qui lançait un renouvellement
 * par requête tombée en 401, provoquerait maintenant des déconnexions
 * inexpliquées dès que deux appels expirent ensemble.
 */
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = useAuth.getState().accessToken;
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    if (
      request.url.endsWith('/v1/auth/refresh') ||
      request.url.endsWith('/v1/auth/login') ||
      request.url.endsWith('/v1/auth/logout')
    ) {
      return response;
    }

    const token = await refreshSession();
    if (!token) return response;

    // Une requête tombée en 401 puis rattrapée traduit quelqu'un qui travaille :
    // sans ça, une page active mais dont le jeton vient d'expirer compterait
    // comme inactive.
    noteActivity();

    const retryHeaders = new Headers(request.headers);
    retryHeaders.set('Authorization', `Bearer ${token}`);
    return fetch(request.url, {
      method: request.method,
      headers: retryHeaders,
      body: request.body,
    });
  },
};

export const apiClient = createClient<paths>({ baseUrl: BASE_URL });
apiClient.use(authMiddleware);
